import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import "./MazeGame.css";

type Direction = "N" | "E" | "S" | "W";
type Command = "MOVE" | "TURN_LEFT" | "TURN_RIGHT";
type Tile = "EMPTY" | "WALL" | "GOAL";
type MoveResult =
  | { ok: true; next: { row: number; col: number } }
  | { ok: false; reason: "wall" | "bounds" };

interface Level {
  id: number;
  name: string;
  grid: string[];
  maxSteps?: number;
}

interface MazeGameProps {
  onBack: () => void;
}

const COMMAND_LABELS: Record<Command, string> = {
  MOVE: "Idź",
  TURN_LEFT: "Skręć w lewo",
  TURN_RIGHT: "Skręć w prawo",
};

const ICONS = {
  move: `${process.env.PUBLIC_URL}/icons/move.png`,
  left: `${process.env.PUBLIC_URL}/icons/rotate_left.png`,
  right: `${process.env.PUBLIC_URL}/icons/rotate_right.png`,
  run: `${process.env.PUBLIC_URL}/icons/run_program.png`,
  trash: `${process.env.PUBLIC_URL}/icons/trash.png`,
};

const LEVELS: Level[] = [
  {
    id: 1,
    name: "Poziom 1",
    grid: [
      "#######",
      "#>..G.#",
      "#.....#",
      "#.....#",
      "#.....#",
      "#.....#",
      "#######",
    ],
  },
  {
    id: 2,
    name: "Poziom 2",
    grid: [
      "#######",
      "#..G..#",
      "#..#..#",
      "#..#..#",
      "#.^...#",
      "#.....#",
      "#######",
    ],
  },
  {
    id: 3,
    name: "Poziom 3",
    grid: [
      "#######",
      "#..#G.#",
      "#..#..#",
      "#..#..#",
      "#..#..#",
      "#.<...#",
      "#######",
    ],
  },
  {
    id: 4,
    name: "Poziom 4",
    grid: [
      "########",
      "#>.....#",
      "###.####",
      "#.....G#",
      "#.######",
      "#......#",
      "########",
    ],
  },
  {
    id: 5,
    name: "Poziom 5",
    grid: [
      "########",
      "#..G...#",
      "#.####.#",
      "#......#",
      "#.####.#",
      "#...^..#",
      "########",
    ],
  },
  {
    id: 6,
    name: "Poziom 6",
    grid: [
      "########",
      "#...#..#",
      "#.#.#G.#",
      "#.#.##.#",
      "#.#....#",
      "#v#####.",
      "########",
    ],
  },
  {
    id: 7,
    name: "Poziom 7",
    grid: [
      "########",
      "#..#...#",
      "#..#.#G#",
      "#..#.#.#",
      "#..#.#.#",
      "#..#.#.#",
      "#>.....#",
      "########",
    ],
  },
  {
    id: 8,
    name: "Poziom 8",
    grid: [
      "#########",
      "#...#...#",
      "#.#.#.#G#",
      "#.#.#.#.#",
      "#.#...#.#",
      "#.#####.#",
      "#.<.....#",
      "#########",
    ],
  },
  {
    id: 9,
    name: "Poziom 9",
    grid: [
      "#########",
      "#..#....#",
      "#..#.##.#",
      "#..#..#G#",
      "#..##.#.#",
      "#....#..#",
      "#^...#..#",
      "#########",
    ],
  },
  {
    id: 10,
    name: "Poziom 10",
    grid: [
      "#########",
      "#..#....#",
      "#..#.##.#",
      "#..#..#.#",
      "#..##.#G#",
      "#....#..#",
      "#>...#..#",
      "#########",
    ],
  },
  {
    id: 11,
    name: "Poziom 11",
    grid: [
      "#########",
      "#..#..G.#",
      "#..#.##.#",
      "#..#..#.#",
      "#..##.#.#",
      "#....#..#",
      "#.<..#..#",
      "#########",
    ],
    maxSteps: 12,
  },
  {
    id: 12,
    name: "Poziom 12",
    grid: [
      "#########",
      "#..#....#",
      "#..#.##.#",
      "#..#..#.#",
      "#..##.#.#",
      "#..G.#..#",
      "#v...#..#",
      "#########",
    ],
    maxSteps: 14,
  },
  {
    id: 13,
    name: "Poziom 13",
    grid: [
      "#########",
      "#..#....#",
      "#..#.##.#",
      "#..#..#.#",
      "#..##.#.#",
      "#..#..#G#",
      "#>...#..#",
      "#########",
    ],
    maxSteps: 16,
  },
  {
    id: 14,
    name: "Poziom 14",
    grid: [
      "#########",
      "#..#....#",
      "#..#.##.#",
      "#..#..#.#",
      "#..##.#.#",
      "#..#..#.#",
      "#..#..#G#",
      "#^.....#.",
      "#########",
    ],
    maxSteps: 18,
  },
  {
    id: 15,
    name: "Poziom 15",
    grid: [
      "#########",
      "#..#....#",
      "#..#.##.#",
      "#..#..#.#",
      "#..##.#.#",
      "#..#..#.#",
      "#..#..#G#",
      "#<.....#.",
      "#########",
    ],
    maxSteps: 18,
  },
];

const VOICE_BASE = `${process.env.PUBLIC_URL}/voice/lector/pl`;

const DIR_ORDER: Direction[] = ["N", "E", "S", "W"];
const DIR_ARROW: Record<Direction, string> = {
  N: "↑",
  E: "→",
  S: "↓",
  W: "←",
};

const parseLevel = (level: Level) => {
  const tiles: Tile[][] = [];
  let start = { row: 0, col: 0 };
  let direction: Direction = "N";
  let goal = { row: 0, col: 0 };

  level.grid.forEach((line, row) => {
    const rowTiles: Tile[] = [];
    line.split("").forEach((char, col) => {
      if (char === "#") {
        rowTiles.push("WALL");
      } else if (char === "G") {
        rowTiles.push("GOAL");
        goal = { row, col };
      } else if ("^>v<".includes(char)) {
        start = { row, col };
        direction =
          char === "^" ? "N" : char === ">" ? "E" : char === "v" ? "S" : "W";
        rowTiles.push("EMPTY");
      } else {
        rowTiles.push("EMPTY");
      }
    });
    tiles.push(rowTiles);
  });

  return { tiles, start, direction, goal };
};

const playAudio = async (url: string, volume = 0.8): Promise<void> => {
  const audio = new Audio(url);
  audio.volume = volume;
  await audio.play();
};

const playSfx = async (name: "success" | "fail"): Promise<void> => {
  const base = `${process.env.PUBLIC_URL}/music/${name}`;
  try {
    await playAudio(`${base}.mp3`, 0.8);
  } catch {
    await playAudio(`${base}.wav`, 0.8);
  }
};

const playOptionalVoice = async (url: string): Promise<void> => {
  try {
    await playAudio(url, 0.8);
  } catch {
    // ignore missing voice lines
  }
};

export function MazeGame({ onBack }: MazeGameProps) {
  const [levelIndex, setLevelIndex] = useState(0);
  const level = LEVELS[levelIndex];
  const parsed = useMemo(() => parseLevel(level), [level]);
  const [program, setProgram] = useState<Command[]>([]);
  const [position, setPosition] = useState(parsed.start);
  const [direction, setDirection] = useState<Direction>(parsed.direction);
  const [stepIndex, setStepIndex] = useState(0);
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [status, setStatus] = useState<
    "idle" | "running" | "failed" | "success"
  >("idle");
  const [error, setError] = useState<"wall" | "bounds" | "ended" | null>(null);
  const [failedStep, setFailedStep] = useState<number | null>(null);
  const [stepsUsed, setStepsUsed] = useState(0);
  const timerRef = useRef<number | null>(null);
  const statusRef = useRef(status);
  const programRef = useRef(program);
  const positionRef = useRef(position);
  const directionRef = useRef(direction);
  const stepIndexRef = useRef(stepIndex);

  const resetExecution = () => {
    setPosition(parsed.start);
    setDirection(parsed.direction);
    setStepIndex(0);
    setActiveStep(null);
    setStatus("idle");
    setError(null);
    setFailedStep(null);
    setStepsUsed(0);
    positionRef.current = parsed.start;
    directionRef.current = parsed.direction;
    stepIndexRef.current = 0;
    statusRef.current = "idle";
  };

  useEffect(() => {
    resetExecution();
  }, [parsed.start.row, parsed.start.col, parsed.direction]);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    programRef.current = program;
  }, [program]);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    directionRef.current = direction;
  }, [direction]);

  useEffect(() => {
    stepIndexRef.current = stepIndex;
  }, [stepIndex]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  const addCommand = (command: Command) => {
    if (status === "running") {
      return;
    }
    setProgram((prev) => [...prev, command]);
    resetExecution();
  };

  const undoCommand = () => {
    if (status === "running") {
      return;
    }
    setProgram((prev) => prev.slice(0, -1));
    resetExecution();
  };

  const clearProgram = async () => {
    if (status === "running") {
      return;
    }
    setProgram([]);
    resetExecution();
    await playOptionalVoice(`${VOICE_BASE}/ui/wyczysc_program.mp3`);
  };

  const rotate = (dir: Direction, command: Command): Direction => {
    const index = DIR_ORDER.indexOf(dir);
    if (command === "TURN_LEFT") {
      return DIR_ORDER[(index + 3) % 4];
    }
    return DIR_ORDER[(index + 1) % 4];
  };

  const attemptMove = (pos: typeof position, dir: Direction): MoveResult => {
    const delta =
      dir === "N"
        ? { row: -1, col: 0 }
        : dir === "S"
          ? { row: 1, col: 0 }
          : dir === "E"
            ? { row: 0, col: 1 }
            : { row: 0, col: -1 };
    const next = { row: pos.row + delta.row, col: pos.col + delta.col };
    if (
      next.row < 0 ||
      next.col < 0 ||
      next.row >= parsed.tiles.length ||
      next.col >= parsed.tiles[0].length
    ) {
      return { ok: false, reason: "bounds" };
    }
    const tile = parsed.tiles[next.row][next.col];
    if (tile === "WALL") {
      return { ok: false, reason: "wall" };
    }
    return { ok: true, next };
  };

  const handleFailure = async (reason: "wall" | "bounds" | "ended") => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setStatus("failed");
    statusRef.current = "failed";
    setError(reason);
    await playSfx("fail");
    if (reason === "wall") {
      await playOptionalVoice(`${VOICE_BASE}/fail/ups_sciana.mp3`);
    } else if (reason === "bounds") {
      await playOptionalVoice(
        `${VOICE_BASE}/fail/nie_mozesz_wyjsc_poza_plansze.mp3`,
      );
    } else {
      await playOptionalVoice(`${VOICE_BASE}/fail/sprobuj_jeszcze_raz.mp3`);
    }
  };

  const handleSuccess = async () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setStatus("success");
    statusRef.current = "success";
    await playSfx("success");
    await playOptionalVoice(`${VOICE_BASE}/success/brawo.mp3`);
  };

  const stepOnce = async () => {
    if (statusRef.current === "success") {
      return;
    }
    if (stepIndexRef.current >= programRef.current.length) {
      setFailedStep(stepIndexRef.current);
      await handleFailure("ended");
      return;
    }
    const command = programRef.current[stepIndexRef.current];
    setActiveStep(stepIndexRef.current);
    setStepsUsed((prev) => prev + 1);

    if (command === "TURN_LEFT" || command === "TURN_RIGHT") {
      const nextDir = rotate(directionRef.current, command);
      setDirection(nextDir);
      directionRef.current = nextDir;
    } else {
      const moveResult = attemptMove(positionRef.current, directionRef.current);
      if (!moveResult.ok) {
        setFailedStep(stepIndexRef.current);
        await handleFailure(moveResult.reason);
        return;
      }
      const nextPos = moveResult.next;
      setPosition(nextPos);
      positionRef.current = nextPos;
      if (parsed.tiles[nextPos.row][nextPos.col] === "GOAL") {
        await handleSuccess();
        return;
      }
    }

    setStepIndex((prev) => prev + 1);
    stepIndexRef.current += 1;
  };

  const runProgram = async () => {
    if (statusRef.current === "running" || programRef.current.length === 0) {
      return;
    }
    resetExecution();
    setStatus("running");
    statusRef.current = "running";

    const tick = async () => {
      if (statusRef.current !== "running") {
        return;
      }
      await stepOnce();
      if (statusRef.current === "running") {
        timerRef.current = window.setTimeout(tick, 450);
      }
    };
    timerRef.current = window.setTimeout(tick, 300);
  };

  const stopProgram = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    resetExecution();
  };

  const stepProgram = async () => {
    if (statusRef.current === "running") {
      return;
    }
    if (statusRef.current === "success" || statusRef.current === "failed") {
      resetExecution();
    }
    await playOptionalVoice(`${VOICE_BASE}/ui/krok_po_kroku.mp3`);
    await stepOnce();
  };

  const goNextLevel = () => {
    setProgram([]);
    setLevelIndex((prev) => (prev + 1) % LEVELS.length);
    resetExecution();
  };

  const goPrevLevel = () => {
    setProgram([]);
    setLevelIndex((prev) => (prev - 1 + LEVELS.length) % LEVELS.length);
    resetExecution();
  };

  return (
    <div className="MazeGame">
      <div className="MazeHeader">
        <div>
          <div className="MazeTitle">Labirynt</div>
          <div className="MazeSubtitle">Zaprogramuj piłkę do mety</div>
        </div>
        <button className="MazeBack" type="button" onClick={onBack}>
          Menu
        </button>
      </div>

      <div className="MazeLayout">
        <div className="MazeBoardPanel">
          <div className="MazeLevelBar">
            <button
              type="button"
              onClick={goPrevLevel}
              aria-label="Poprzedni poziom"
              className="MazeIconButton MazeNavButton"
            >
              <span className="MazeChevron">‹</span>
            </button>
            <div>
              <div className="MazeLevelName">{level.name}</div>
              {level.maxSteps && (
                <div className="MazeLevelHint">
                  Cel: ≤ {level.maxSteps} kroków
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={goNextLevel}
              aria-label="Następny poziom"
              className="MazeIconButton MazeNavButton"
            >
              <span className="MazeChevron">›</span>
            </button>
          </div>

          <div
            className="MazeGrid"
            style={
              {
                "--cols": parsed.tiles[0].length,
                "--rows": parsed.tiles.length,
              } as CSSProperties
            }
          >
            {parsed.tiles.map((row, rowIndex) =>
              row.map((tile, colIndex) => {
                const isBall =
                  position.row === rowIndex && position.col === colIndex;
                return (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    className={`MazeTile ${
                      tile === "WALL"
                        ? "is-wall"
                        : tile === "GOAL"
                          ? "is-goal"
                          : ""
                    }`}
                  >
                    {isBall && (
                      <div className="MazeBall">
                        <span className="MazeArrow">
                          {DIR_ARROW[direction]}
                        </span>
                      </div>
                    )}
                  </div>
                );
              }),
            )}
          </div>
          {status === "failed" && error && (
            <div className="MazeMessage is-error">
              {error === "wall"
                ? "Ups, ściana."
                : error === "bounds"
                  ? "Nie możesz wyjść poza planszę."
                  : "Program się skończył."}
            </div>
          )}
          {status === "success" && (
            <div className="MazeMessage is-success">Brawo!</div>
          )}
        </div>

        <div className="MazeProgramPanel">
          <div className="MazePalette">
            <button
              type="button"
              onClick={() => addCommand("MOVE")}
              disabled={status === "running"}
              aria-label={COMMAND_LABELS.MOVE}
              className="MazeIconButton"
            >
              <img src={ICONS.move} alt="" />
            </button>
            <button
              type="button"
              onClick={() => addCommand("TURN_LEFT")}
              disabled={status === "running"}
              aria-label={COMMAND_LABELS.TURN_LEFT}
              className="MazeIconButton"
            >
              <img src={ICONS.left} alt="" />
            </button>
            <button
              type="button"
              onClick={() => addCommand("TURN_RIGHT")}
              disabled={status === "running"}
              aria-label={COMMAND_LABELS.TURN_RIGHT}
              className="MazeIconButton"
            >
              <img src={ICONS.right} alt="" />
            </button>
          </div>

          <div className="MazeProgramList">
            {program.length === 0 && (
              <div className="MazeProgramEmpty">Dodaj kroki programu.</div>
            )}
            {program.map((command, index) => {
              const isActive = index === activeStep;
              const isFailed = status === "failed" && index === failedStep;
              return (
                <div
                  key={`${command}-${index}`}
                  className={`MazeProgramStep ${
                    isActive ? "is-active" : ""
                  } ${isFailed ? "is-failed" : ""}`}
                  aria-label={`${index + 1}. ${COMMAND_LABELS[command]}`}
                >
                  <span className="MazeProgramIndex">{index + 1}</span>
                  <span className="MazeProgramIcon">
                    <img
                      src={
                        command === "MOVE"
                          ? ICONS.move
                          : command === "TURN_LEFT"
                            ? ICONS.left
                            : ICONS.right
                      }
                      alt=""
                    />
                  </span>
                </div>
              );
            })}
          </div>

          <div className="MazeControls">
            <button
              type="button"
              onClick={runProgram}
              disabled={status === "running" || program.length === 0}
              aria-label="Start"
              className="MazeIconButton is-primary"
            >
              <img src={ICONS.run} alt="" />
            </button>
            <button
              type="button"
              onClick={stopProgram}
              disabled={
                status !== "running" &&
                status !== "failed" &&
                status !== "success"
              }
              aria-label="Stop"
              className="MazeIconButton"
            >
              <span className="MazeStopIcon" />
            </button>
            <button
              type="button"
              onClick={stepProgram}
              disabled={program.length === 0 || status === "running"}
              aria-label="Krok"
              className="MazeIconButton"
            >
              <span className="MazeStepIcon" />
            </button>
            <button
              type="button"
              onClick={undoCommand}
              disabled={program.length === 0 || status === "running"}
              aria-label="Cofnij"
              className="MazeIconButton"
            >
              <span className="MazeUndoIcon" />
            </button>
            <button
              type="button"
              onClick={clearProgram}
              disabled={program.length === 0 || status === "running"}
              aria-label="Wyczyść"
              className="MazeIconButton is-danger"
            >
              <img src={ICONS.trash} alt="" />
            </button>
          </div>

          <div className="MazeFooter">Kroki: {stepsUsed}</div>
        </div>
      </div>
    </div>
  );
}
